import { serverGet } from "../../middleware/http";
import { apiConst } from "../constant/constant";

// SSO entry -> first page handoff.
//
// /sso (security/entry/Entry.js) holds the patient on the 4-step loader for a few seconds and then
// leaves with a full page load (window.location.href -- the SPA booted before sign-in, so a client
// side navigate is not an option). Everything the landing page would fetch on mount is therefore
// fetched again from scratch, behind a loader, right after the patient already waited.
//
// Entry.js uses that wait instead: once sign-in has succeeded and the destination is known, it
// fetches the landing page's first requests (the same URLs, after auth, so the same answers) and
// stores them here. The landing page reads them through handoffGet() instead of the network.
//
// Deliberately narrow, so no existing flow can see stale data:
//   - one-shot: an entry is removed the moment it is read; a later visit to the same page fetches.
//   - short-lived: entries older than MAX_AGE_MS are ignored and dropped.
//   - only filled by Entry.js; every other flow finds the store empty and falls through to
//     serverGet exactly as before.
//   - account/service configuration only -- never patient records.
const STORE_KEY = "cpSsoHandoff";
const MAX_AGE_MS = 60 * 1000;

const read = () => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
        return stored && typeof stored === "object" ? stored : {};
    } catch (e) {
        return {};
    }
};

const write = (entries) => {
    try {
        if (Object.keys(entries).length) sessionStorage.setItem(STORE_KEY, JSON.stringify(entries));
        else sessionStorage.removeItem(STORE_KEY);
        return true;
    } catch (e) {
        // Quota exceeded / storage unavailable -- the landing page simply fetches for itself.
        console.error(e);
        return false;
    }
};

// Same test as requestCache.js: serverGet resolves with undefined / an error response instead of
// throwing, and handing either over would pin a failure the landing page could have retried.
const isUsable = (res) => !!res && res.status == 1;

/**
 * Fetches url now and keeps the response for the next page load. Resolves when the entry is
 * stored (or skipped); never rejects, so callers can await it without a try/catch.
 */
export const stashHandoff = async (url) => {
    try {
        const res = await serverGet(url);
        if (!isUsable(res)) return;

        const entries = read();
        entries[url] = { at: Date.now(), res };

        if (!write(entries)) {
            // One oversized payload must not cost the others -- keep what was already stored.
            delete entries[url];
            write(entries);
        }
    } catch (e) {
        console.error(e);
    }
};

// The SSO entry pages themselves never read the handoff: whatever is stored there belongs to an
// earlier entry (Entry.js clears it and fills it again only after the new sign-in succeeds).
const isEntryPage = () => {
    try {
        return /\/(sso|link)\/?$/i.test(window.location.pathname);
    } catch (e) {
        return false;
    }
};

/** Returns and removes the stored response for url, or null when there is none (or it expired). */
export const takeHandoff = (url) => {
    if (isEntryPage()) return null;

    const entries = read();
    if (!Object.keys(entries).length) return null;

    const now = Date.now();
    const hit = entries[url];

    delete entries[url];
    Object.keys(entries).forEach((key) => {
        if (!(now - entries[key]?.at < MAX_AGE_MS)) delete entries[key];
    });
    write(entries);

    return hit && now - hit.at < MAX_AGE_MS ? hit.res : null;
};

/** Drop-in for serverGet(url) on a landing page: serves the SSO handoff once, otherwise fetches. */
export const handoffGet = (url) => {
    const res = takeHandoff(url);
    return res ? Promise.resolve(res) : serverGet(url);
};

// The URL is the join key, so each one the landing page requests is built here and imported by
// both sides (Entry.js and the consumer) -- two copies of the same template literal would drift.

/** LanguageContext's language resource read -- every page load makes it. */
export const languageResourceUrl = () => `${apiConst.getlangresource}?ln=${"en-us"}`;

/** Service.js's service record read -- same id-then-code order Service.js builds. */
export const serviceRecordUrl = (serviceId, serviceCode) => {
    const params = new URLSearchParams();
    if (serviceId) params.append("id", serviceId);
    if (serviceCode) params.append("code", serviceCode);
    const qs = params.toString();
    return `${apiConst.getService}${qs ? `?${qs}` : ""}`;
};

/** ServiceList.js's service-type read -- exactly the URL ServiceList has always built. */
export const serviceTypeUrl = ({ typeId, serviceId, followUpServiceId, isFollowup, providerMemberId } = {}) => {
    let url = `${apiConst.getServiceType}?typeId=${typeId ? typeId : 1}${serviceId || followUpServiceId ? `&serviceid=${isFollowup ? followUpServiceId : serviceId ? serviceId : ""}` : ""}`;
    if (providerMemberId) url += `&providerMemberId=${providerMemberId}`;
    return url;
};

/** Removes every entry -- a new SSO entry starts clean, and logout must not leave one behind. */
export const clearHandoff = () => {
    try {
        sessionStorage.removeItem(STORE_KEY);
    } catch (e) {
        console.error(e);
    }
};
